import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  FormControlLabel,
  Checkbox,
  IconButton,
  Typography,
  CircularProgress,
  Tooltip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Add, Delete } from '@material-ui/icons';
import ReactJson from 'react-json-view';
import { createProgramGroup } from '../../utilities/mezzanineApi';
import { useMezzanine } from '../../context/MezzanineContext';

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    fontSize: '12px',
    '& .MuiInputBase-root': {
      fontSize: '12px',
    },
    '& .MuiInputLabel-root': {
      fontSize: '12px',
    },
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: 'bold',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    color: '#ff6f00',
  },
  audioCard: {
    padding: theme.spacing(1.5),
    border: '1px solid #ccc',
    borderRadius: '4px',
    backgroundColor: '#f0f0f0',
    marginBottom: theme.spacing(1.5),
  },
  channelIndicator: {
    display: 'flex',
    gap: '4px',
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1),
    backgroundColor: '#f0f0f0',
    border: '1px solid #ccc',
    borderRadius: '4px',
  },
  channelBox: {
    width: '26px',
    height: '26px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    fontSize: '10px',
  },
  fieldRow: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  audioScrollContainer: {
    maxHeight: '250px',
    overflowY: 'auto',
    paddingRight: '4px',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#f5f5f5',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#ff6f00',
      borderRadius: '4px',
      '&:hover': {
        backgroundColor: '#e65100',
      },
    },
  },
}));

const LANGUAGES = [
  { value: 'eng', label: 'English' },
  { value: 'hin', label: 'Hindi' },
  { value: 'spa', label: 'Spanish' },
  { value: 'fra', label: 'French' },
  { value: 'deu', label: 'German' },
  { value: 'ita', label: 'Italian' },
  { value: 'por', label: 'Portuguese' },
  { value: 'jpn', label: 'Japanese' },
  { value: 'kor', label: 'Korean' },
  { value: 'ara', label: 'Arabic' },
  { value: 'tur', label: 'Turkish' },
  { value: 'tha', label: 'Thai' },
  { value: 'ind', label: 'Indonesian' },
  { value: 'ell', label: 'Greek' },
];

const CHANNEL_MODES = [
  { value: 'STEREO', label: 'Stereo', channels: 2 },
  { value: 'SURROUND_51', label: '5.1 Surround', channels: 6 },
];

const getChannelOptions = (mode) => {
  if (mode === 'STEREO') {
    return [
      { label: 'Embedded 1-2', value: [1, 2], mapping: 'LR' },
      { label: 'Embedded 3-4', value: [3, 4], mapping: 'LR' },
      { label: 'Embedded 5-6', value: [5, 6], mapping: 'LR' },
      { label: 'Embedded 7-8', value: [7, 8], mapping: 'LR' },
      { label: 'Embedded 9-10', value: [9, 10], mapping: 'LR' },
      { label: 'Embedded 11-12', value: [11, 12], mapping: 'LR' },
      { label: 'Embedded 13-14', value: [13, 14], mapping: 'LR' },
      { label: 'Embedded 15-16', value: [15, 16], mapping: 'LR' },
    ];
  } else if (mode === 'SURROUND_51') {
    return [
      { label: 'Embedded 1-6', value: [1, 2, 3, 4, 5, 6], mapping: 'LR_CLfe_LsRs' },
      { label: 'Embedded 3-8', value: [3, 4, 5, 6, 7, 8], mapping: 'LR_CLfe_LsRs' },
      { label: 'Embedded 9-14', value: [9, 10, 11, 12, 13, 14], mapping: 'LR_CLfe_LsRs' },
      { label: 'Embedded 11-16', value: [11, 12, 13, 14, 15, 16], mapping: 'LR_CLfe_LsRs' },
    ];
  }
  return [];
};

const CreateProgramGroupForm = ({ open, onClose, onSuccess, onNotify }) => {
  const classes = useStyles();
  const { profiles, profilesLoading } = useMezzanine();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vancEnabled, setVancEnabled] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    video: { profile_label: '', pid: 270, timecode_source: 'OFF' },
    color: { profile_label: '' },
    output_bitrate: 35000,
    srt_latency: 500,
    pcr_pid: 270,
    pmt_pid: 32,
    pcr_interval: 35,
    service_id: 1,
    service_provider: 'Amazon',
  });

  const [audios, setAudios] = useState([]);
  const [vanc, setVanc] = useState({ profile_label: '', pid: 452, uid: 0 });

  // Filter profiles by type
  const videoProfiles = useMemo(() => 
    profiles.filter(p => p.profile_type === 'video'), [profiles]);
  const audioProfiles = useMemo(() => 
    profiles.filter(p => p.profile_type === 'audio'), [profiles]);
  const colorProfiles = useMemo(() => 
    profiles.filter(p => p.profile_type === 'color'), [profiles]);
  const vancProfiles = useMemo(() => 
    profiles.filter(p => p.profile_type === 'vanc'), [profiles]);

  // Get all used channels
  const usedChannels = useMemo(() => {
    const channels = new Set();
    audios.forEach(audio => {
      audio.source_channels.forEach(ch => channels.add(ch));
    });
    return channels;
  }, [audios]);

  // Add audio
  const addAudio = () => {
    const newAudio = {
      uid: audios.length,
      profile_label: '',
      pid: 969 + audios.length,
      language: 'eng',
      channel_mode: 'STEREO',
      channel_mapping: 'LR',
      source_channels: [],
      channelRange: '',
      codec: 'PCM',
      audio_type: 'UNDEFINED',
      passthrough: false,
    };
    setAudios([...audios, newAudio]);
  };

  // Remove audio
  const removeAudio = (index) => {
    const newAudios = audios.filter((_, i) => i !== index);
    // Re-index UIDs and PIDs
    newAudios.forEach((audio, i) => {
      audio.uid = i;
      audio.pid = 969 + i;
    });
    setAudios(newAudios);
  };

  // Update audio field
  const updateAudio = (index, field, value) => {
    const newAudios = [...audios];
    newAudios[index][field] = value;

    // Reset channels when mode changes
    if (field === 'channel_mode') {
      newAudios[index].channelRange = '';
      newAudios[index].source_channels = [];
      newAudios[index].channel_mapping = value === 'STEREO' ? 'LR' : 'LR_CLfe_LsRs';
    }

    // Set channels when range is selected
    if (field === 'channelRange') {
      const option = getChannelOptions(newAudios[index].channel_mode).find(opt => opt.label === value);
      if (option) {
        newAudios[index].source_channels = option.value;
        newAudios[index].channel_mapping = option.mapping;
      }
    }

    setAudios(newAudios);
  };

  // Handle form field change
  const handleChange = (field, subfield = null) => (event) => {
    const value = event.target.value;
    if (subfield) {
      setFormData({
        ...formData,
        [field]: { ...formData[field], [subfield]: value }
      });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  // Handle VANC toggle
  const handleVancToggle = (event) => {
    setVancEnabled(event.target.checked);
  };

  // Build the payload object for JSON preview
  const buildPayload = () => {
    return {
      name: formData.name,
      video: {
        profile_label: formData.video.profile_label,
        pid: parseInt(formData.video.pid) || 270,
        timecode_source: formData.video.timecode_source,
      },
      audios: audios.map(audio => ({
        profile_label: audio.profile_label,
        pid: parseInt(audio.pid) || 969,
        uid: audio.uid,
        language: audio.language,
        channel_mode: audio.channel_mode,
        channel_mapping: audio.channel_mapping,
        source_channels: audio.source_channels,
        codec: audio.codec,
        audio_type: audio.audio_type,
        passthrough: audio.passthrough,
        loudness: {},
        backup: {},
        num_au_per_pes: {},
        lip_sync_adjustment: {},
        latency_adjustment: {},
        level_adjustment: {},
        dolby_e_prog_num: {},
        component_priority_list: {},
      })),
      color: {
        profile_label: formData.color.profile_label,
      },
      vancs: vancEnabled ? [{
        profile_label: vanc.profile_label,
        pid: parseInt(vanc.pid) || 452,
        uid: vanc.uid,
      }] : [],
      output_bitrate: parseInt(formData.output_bitrate) || 35000,
      srt_latency: parseInt(formData.srt_latency) || 500,
      pcr_pid: parseInt(formData.pcr_pid) || 270,
      pmt_pid: parseInt(formData.pmt_pid) || 32,
      pcr_interval: parseFloat(formData.pcr_interval) || 35,
      service_id: parseInt(formData.service_id) || 1,
      service_provider: formData.service_provider,
    };
  };

  // Handle JSON editor changes
  const handleJsonEdit = (edit) => {
    const updated = edit.updated_src;
    
    // Update form state from JSON
    setFormData({
      name: updated.name || '',
      video: {
        profile_label: updated.video?.profile_label || '',
        pid: updated.video?.pid || 270,
        timecode_source: updated.video?.timecode_source || 'OFF',
      },
      color: {
        profile_label: updated.color?.profile_label || '',
      },
      output_bitrate: updated.output_bitrate || 35000,
      srt_latency: updated.srt_latency || 500,
      pcr_pid: updated.pcr_pid || 270,
      pmt_pid: updated.pmt_pid || 32,
      pcr_interval: updated.pcr_interval || 35,
      service_id: updated.service_id || 1,
      service_provider: updated.service_provider || 'Amazon',
    });

    // Update audios
    if (updated.audios) {
      setAudios(updated.audios.map((audio, idx) => ({
        uid: audio.uid ?? idx,
        profile_label: audio.profile_label || '',
        pid: audio.pid || 969 + idx,
        language: audio.language || 'eng',
        channel_mode: audio.channel_mode || 'STEREO',
        channel_mapping: audio.channel_mapping || 'LR',
        source_channels: audio.source_channels || [],
        channelRange: '',
        codec: audio.codec || 'PCM',
        audio_type: audio.audio_type || 'UNDEFINED',
        passthrough: audio.passthrough || false,
      })));
    }

    // Update VANC
    if (updated.vancs && updated.vancs.length > 0) {
      setVancEnabled(true);
      setVanc({
        profile_label: updated.vancs[0].profile_label || '',
        pid: updated.vancs[0].pid || 452,
        uid: updated.vancs[0].uid || 0,
      });
    } else {
      setVancEnabled(false);
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = buildPayload();
      await createProgramGroup(payload);

      if (onNotify) {
        onNotify({ message: 'Program group created successfully', severity: 'success' });
      }
      onSuccess && onSuccess();
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to create program group');
    } finally {
      setLoading(false);
    }
  };

  // Handle close
  const handleClose = () => {
    setFormData({
      name: '',
      video: { profile_label: '', pid: 270, timecode_source: 'OFF' },
      color: { profile_label: '' },
      output_bitrate: 35000,
      srt_latency: 500,
      pcr_pid: 270,
      pmt_pid: 32,
      pcr_interval: 35,
      service_id: 1,
      service_provider: 'Amazon',
    });
    setAudios([]);
    setVancEnabled(false);
    setVanc({ profile_label: '', pid: 452, uid: 0 });
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth PaperProps={{ style: { height: '85vh' } }}>
      <DialogTitle style={{ borderBottom: '1px solid #eee', padding: '16px 20px' }}>
        Create Program Group
      </DialogTitle>
      <DialogContent style={{ padding: 0, overflow: 'hidden' }}>
        <Box display="flex" style={{ height: '100%' }}>
          {/* Left side - Form */}
          <Box flex={0.45} style={{ padding: '20px', overflowY: 'auto' }}>
            <Box className={classes.form}>
              {error && (
                <Typography color="error" style={{ fontSize: '12px', marginBottom: '8px' }}>
                  {error}
                </Typography>
              )}

              {/* Name */}
              <TextField
                label="Name"
                variant="outlined"
            size="small"
            fullWidth
            value={formData.name}
            onChange={handleChange('name')}
            required
            placeholder="e.g., HEVC-SDR-eng5.1-engStereo"
          />

          {/* Video Section */}
          <Typography className={classes.sectionTitle}>Video</Typography>
          <Box className={classes.fieldRow}>
            <TextField
              select
              label="Video Profile"
              variant="outlined"
              size="small"
              fullWidth
              value={formData.video.profile_label}
              onChange={handleChange('video', 'profile_label')}
              required
              disabled={profilesLoading}
            >
              {videoProfiles.map(p => (
                <MenuItem key={p.profile_key} value={p.label} style={{ fontSize: '12px' }}>
                  {p.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="PID"
              variant="outlined"
              size="small"
              type="number"
              value={formData.video.pid}
              onChange={handleChange('video', 'pid')}
              style={{ width: '100px' }}
            />
          </Box>
          <TextField
            select
            label="Color Profile"
            variant="outlined"
            size="small"
            fullWidth
            value={formData.color.profile_label}
            onChange={handleChange('color', 'profile_label')}
            required
            disabled={profilesLoading}
            style={{ marginTop: '12px' }}
          >
            {colorProfiles.map(p => (
              <MenuItem key={p.profile_key} value={p.label} style={{ fontSize: '12px' }}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>

          {/* VANC Section */}
          <FormControlLabel
            control={
              <Checkbox
                checked={vancEnabled}
                onChange={handleVancToggle}
                size="small"
                style={{ color: '#ff6f00' }}
              />
            }
            label={<span style={{ fontSize: '12px' }}>Enable VANC</span>}
          />
          {vancEnabled && (
            <Box className={classes.fieldRow}>
              <TextField
                select
                label="VANC Profile"
                variant="outlined"
                size="small"
                fullWidth
                value={vanc.profile_label}
                onChange={(e) => setVanc({ ...vanc, profile_label: e.target.value })}
                disabled={profilesLoading}
              >
                {vancProfiles.map(p => (
                  <MenuItem key={p.profile_key} value={p.label} style={{ fontSize: '12px' }}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="PID"
                variant="outlined"
                size="small"
                type="number"
                value={vanc.pid}
                onChange={(e) => setVanc({ ...vanc, pid: e.target.value })}
                style={{ width: '100px' }}
              />
            </Box>
          )}

          {/* Audios Section */}
          <Box display="flex" alignItems="center" style={{ marginTop: '16px' }}>
            <Typography className={classes.sectionTitle} style={{ marginTop: 0, marginBottom: 0 }}>
              Audios
            </Typography>
            <IconButton size="small" onClick={addAudio} style={{ color: '#ff6f00', marginLeft: '8px' }}>
              <Add fontSize="small" />
            </IconButton>
          </Box>

          {/* Channel Indicator */}
          {audios.length > 0 && (
            <Box className={classes.channelIndicator}>
              {[...Array(16)].map((_, i) => {
                const channelNum = i + 1;
                const isUsed = usedChannels.has(channelNum);
                return (
                  <Box
                    key={channelNum}
                    className={classes.channelBox}
                    style={{
                      border: isUsed ? '2px solid #ff6f00' : '1px solid #ddd',
                      backgroundColor: isUsed ? '#fff3e0' : '#fff',
                      fontWeight: isUsed ? 'bold' : 'normal',
                    }}
                  >
                    {channelNum}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Audio Cards */}
          <Box className={classes.audioScrollContainer}>
            {audios.map((audio, idx) => (
              <Box key={idx} className={classes.audioCard}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography style={{ fontSize: '12px', fontWeight: 'bold' }}>
                    Audio {idx + 1}
                  </Typography>
                  <IconButton size="small" onClick={() => removeAudio(idx)} style={{ color: '#d32f2f', padding: '4px' }}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>

                <Box className={classes.fieldRow} mb={1}>
                  <TextField
                    select
                    label="Language"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={audio.language}
                    onChange={(e) => updateAudio(idx, 'language', e.target.value)}
                  >
                    {LANGUAGES.map(lang => (
                      <MenuItem key={lang.value} value={lang.value} style={{ fontSize: '11px' }}>
                        {lang.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Profile"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={audio.profile_label}
                    onChange={(e) => updateAudio(idx, 'profile_label', e.target.value)}
                    disabled={profilesLoading}
                  >
                    {audioProfiles.map(p => (
                      <MenuItem key={p.profile_key} value={p.label} style={{ fontSize: '11px' }}>
                        {p.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                <Box className={classes.fieldRow} mb={1}>
                  <TextField
                    select
                    label="Mode"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={audio.channel_mode}
                    onChange={(e) => updateAudio(idx, 'channel_mode', e.target.value)}
                  >
                    {CHANNEL_MODES.map(mode => (
                      <MenuItem key={mode.value} value={mode.value} style={{ fontSize: '11px' }}>
                        {mode.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Channels"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={audio.channelRange}
                    onChange={(e) => updateAudio(idx, 'channelRange', e.target.value)}
                  >
                    {getChannelOptions(audio.channel_mode).map(opt => (
                      <MenuItem key={opt.label} value={opt.label} style={{ fontSize: '11px' }}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                <TextField
                  label="PID"
                  variant="outlined"
                  size="small"
                  type="number"
                  value={audio.pid}
                  onChange={(e) => updateAudio(idx, 'pid', parseInt(e.target.value))}
                  style={{ width: '100px' }}
                />
              </Box>
            ))}
          </Box>

          {/* Output Settings */}
          <Typography className={classes.sectionTitle}>Output Settings</Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" style={{ gap: '12px' }}>
            <Tooltip title="Output bitrate in Kbps" arrow placement="top">
              <TextField
                label="SRT Output Bitrate (Kbps)"
                variant="outlined"
                size="small"
                type="number"
                value={formData.output_bitrate}
                onChange={handleChange('output_bitrate')}
                fullWidth
              />
            </Tooltip>
            <TextField
              label="PCR PID"
              variant="outlined"
              size="small"
              type="number"
              value={formData.pcr_pid}
              onChange={handleChange('pcr_pid')}
              fullWidth
            />
            <Tooltip title="SRT latency in milliseconds (ms)" arrow placement="top">
              <TextField
                label="SRT Latency (ms)"
                variant="outlined"
                size="small"
                type="number"
                value={formData.srt_latency}
                onChange={handleChange('srt_latency')}
                fullWidth
              />
            </Tooltip>
            <TextField
              label="PMT PID"
              variant="outlined"
              size="small"
              type="number"
              value={formData.pmt_pid}
              onChange={handleChange('pmt_pid')}
              fullWidth
            />
            <TextField
              label="Service Provider"
              variant="outlined"
              size="small"
              value={formData.service_provider}
              onChange={handleChange('service_provider')}
              fullWidth
            />
            <Tooltip title="PCR interval in milliseconds (ms)" arrow placement="top">
              <TextField
                label="PCR Interval"
                variant="outlined"
                size="small"
                type="number"
                value={formData.pcr_interval}
                onChange={handleChange('pcr_interval')}
                fullWidth
              />
            </Tooltip>
            <TextField
              label="Service ID"
              variant="outlined"
              size="small"
              type="number"
              value={formData.service_id}
              onChange={handleChange('service_id')}
              fullWidth
            />
          </Box>
            </Box>
          </Box>

          {/* Right side - JSON Editor */}
          <Box flex={0.55} style={{ backgroundColor: '#fff8f0', padding: '16px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box style={{ backgroundColor: '#fff8f0', padding: '16px', borderRadius: '4px', fontSize: '12px', overflowY: 'auto', flex: 1 }}>
              <ReactJson
                src={buildPayload()}
                name="program_group"
                collapsed={false}
                onEdit={handleJsonEdit}
                onAdd={handleJsonEdit}
                onDelete={handleJsonEdit}
                displayDataTypes={false}
                displayObjectSize={false}
                theme="bright:inverted"
                style={{ fontSize: '12px', backgroundColor: '#fff8f0' }}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid #eee', padding: '12px 20px' }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.name || !formData.video.profile_label || !formData.color.profile_label}
          style={{ backgroundColor: loading ? '#ccc' : '#ff6f00', color: 'white' }}
        >
          {loading ? <CircularProgress size={20} style={{ color: 'white' }} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProgramGroupForm;
